<template>
  <div class="anamnesis-subset">
    <!-- Toolbar réutilisable -->
    <SubsetToolbar
      :sort-options="sortOptions"
      :sort-by="sortBy"
      :sort-direction="sortDirection"
      :is-card-view="isCardView"
      :search-query="searchQuery"
      search-placeholder="Rechercher un projet..."
      create-button-text="Nouveau projet"
      :show-create-button="true"
      @update:sort-by="handleUpdateSortBy"
      @update:search-query="handleUpdateSearchQuery"
      @toggle-sort-direction="handleToggleSortDirection"
      @toggle-view="handleToggleView"
      @create="$emit('create')"
    >
      <!-- Slot filtres : Dropdown types spécifique aux projets -->
      <template #filters>
        <div class="relative">
          <button
            class="px-3 py-1.5 rounded-full text-sm transition-colors duration-200 flex items-center space-x-1.5"
            :class="[
              selectedTypes.length > 0
                ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            ]"
            @click="handleToggleTypeDropdown"
          >
            <FilterIcon class="h-4 w-4" />
            <span>Types ({{ selectedTypes.length }})</span>
            <ChevronDownIcon class="h-4 w-4" :class="{ 'transform rotate-180': showTypeDropdown }" />
          </button>

          <!-- Dropdown pour les types -->
          <div
            v-if="showTypeDropdown"
            class="absolute z-50 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 divide-y divide-gray-100"
          >
            <div class="px-3 py-2 flex items-center justify-between">
              <button
                class="text-sm text-blue-600 hover:text-blue-800 font-medium"
                @click="handleToggleAllTypes"
              >
                {{ allTypesSelected ? 'Tout désélectionner' : 'Tout sélectionner' }}
              </button>
              <span class="text-xs text-gray-500">{{ selectedTypes.length }} sélectionné(s)</span>
            </div>
            <div class="max-h-48 overflow-y-auto py-1">
              <div
                v-for="type in availableTypes"
                :key="type"
                class="flex items-center px-3 py-1.5 hover:bg-gray-50 cursor-pointer group"
                @click="handleToggleType(type || '')"
              >
                <span class="text-sm text-gray-700 group-hover:text-gray-900 min-w-0 flex-1 mr-4">{{ type || 'Non spécifié' }}</span>
                <div class="flex-shrink-0 w-5">
                  <input
                    type="checkbox"
                    :checked="selectedTypes.includes(type || '')"
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    @change="handleToggleType(type || '')"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </SubsetToolbar>

    <!-- Liste des projets -->
    <div
      v-if="paginatedProjects.length > 0"
      :class="{
        'anamnesis-grid': isCardView,
        'anamnesis-table': !isCardView
      }"
      class="view-transition"
    >
      <!-- En-tête du tableau (mode liste seulement) -->
      <div v-if="!isCardView" class="flex items-center px-4 py-3 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-700">
        <div class="flex-[1.5] min-w-0">Projet</div>
        <div class="hidden md:flex flex-1 min-w-0 px-3">Financeur</div>
        <div class="min-w-0 px-3">Mémos</div>
      </div>
      
      <ProjectCard
        v-for="project in paginatedProjects"
        :key="project.uuid"
        :project="project"
        :view-mode="isCardView ? 'default' : 'table'"
        :show-actions="showActions"
        :show-edit-button="showEditButton"
        :show-delete-button="showDeleteButton"
        :is-owned="isProjectOwned(project)"
        :is-public="project.is_public"
        :show-ownership-badge="true"
        :user-id="project.user_id"
        @edit="$emit('edit', project)"
        @delete="$emit('delete', project.uuid)"
      />
    </div>

    <div v-else class="text-center py-8">
      <p class="text-gray-500">Aucun projet trouvé</p>
    </div>

    <!-- Compteur de projets -->
    <div class="flex justify-end mt-4 mb-2">
      <p class="text-sm text-gray-400">
        {{ filteredProjects.length }} projet{{ filteredProjects.length > 1 ? 's' : '' }} trouvé{{ filteredProjects.length > 1 ? 's' : '' }}
      </p>
    </div>

    <!-- Pagination -->
    <div v-if="filteredProjects.length > itemsPerPage" class="mt-4 flex justify-center space-x-2">
      <button
        :disabled="currentPage === 1"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === 1 ? 'text-gray-400 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="handlePreviousPage"
      >
        Précédent
      </button>
      <button
        v-for="page in totalPages"
        :key="page"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === page ? 'bg-blue-500 text-white border-blue-500' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="currentPage = page"
      >
        {{ page }}
      </button>
      <button
        :disabled="currentPage === totalPages"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === totalPages ? 'text-gray-400 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="handleNextPage"
      >
        Suivant
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
    ChevronDown as ChevronDownIcon,
    Filter as FilterIcon,
} from 'lucide-vue-next';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import type { Project } from '../../api/types/entities/project/types';
import { useAuthStore } from '../../stores/auth';
import type { SortOption } from '../UI/SubsetToolbar.vue';
import SubsetToolbar from '../UI/SubsetToolbar.vue';
import ProjectCard from '../cards/ProjectCard.vue';

// Types stricts
type SortBy = 'name' | 'date' | 'memos';
type SortDirection = 'asc' | 'desc';

// Props avec types stricts
interface Props {
  projects: Project[];
  showActions?: boolean;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  itemsPerPage?: number;
  defaultSort?: SortBy;
}

const props = withDefaults(defineProps<Props>(), {
  showActions: true,
  showEditButton: true,
  showDeleteButton: true,
  itemsPerPage: 20,
  defaultSort: 'name',
});

// Events avec types stricts
defineEmits<{
  create: [];
  edit: [project: Project];
  delete: [uuid: string];
}>();

// Store auth
const authStore = useAuthStore();

// État réactif avec types stricts
const isCardView: Ref<boolean> = ref(true);
const searchQuery: Ref<string> = ref('');
const sortBy: Ref<SortBy> = ref(props.defaultSort);
const sortDirection: Ref<SortDirection> = ref('asc');
const selectedTypes: Ref<string[]> = ref([]);
const showTypeDropdown: Ref<boolean> = ref(false);
const currentPage: Ref<number> = ref(1);

// Options de tri avec types stricts
const sortOptions: ComputedRef<SortOption[]> = computed(() => [
  { value: 'name', label: 'Nom (Acronyme/Titre)' },
  { value: 'date', label: 'Date de création' },
  { value: 'memos', label: 'Nombre de mémos' },
]);

// Types disponibles extraits des projets
const availableTypes: ComputedRef<string[]> = computed(() => {
  const types = new Set(props.projects.map((project: Project) => project.type).filter(Boolean) as string[]);
  return Array.from(types).sort();
});

// Computed pour vérifier si tous les types sont sélectionnés
const allTypesSelected: ComputedRef<boolean> = computed(() => 
  selectedTypes.value.length === availableTypes.value.length
);

// Handlers avec types de retour explicites
const handleUpdateSortBy = (value: string): void => {
  sortBy.value = value as SortBy;
};

const handleUpdateSearchQuery = (value: string): void => {
  searchQuery.value = value;
};

const handleToggleSortDirection = (): void => {
  sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
};

const handleToggleView = (): void => {
  isCardView.value = !isCardView.value;
};

const handleToggleTypeDropdown = (): void => {
  showTypeDropdown.value = !showTypeDropdown.value;
};

// Fonction pour fermer les dropdowns (si nécessaire plus tard)
// const handleCloseDropdowns = (): void => {
//   showTypeDropdown.value = false;
// };

const handleToggleType = (type: string): void => {
  const index: number = selectedTypes.value.indexOf(type);
  if (index === -1) {
    selectedTypes.value.push(type);
  } else {
    selectedTypes.value.splice(index, 1);
  }
};

const handleToggleAllTypes = (): void => {
  if (allTypesSelected.value) {
    selectedTypes.value = [];
  } else {
    selectedTypes.value = [...availableTypes.value.map((type: string | null) => type || '')];
  }
};

const handlePreviousPage = (): void => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const handleNextPage = (): void => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

// Fonction pour vérifier la propriété d'un projet
const isProjectOwned = (project: Project): boolean => {
  return authStore.isAssetOwner(project);
};

// Fonction de recherche typée correctement
const searchInProject = (project: Project, query: string): boolean => {
  const searchTerm = query.toLowerCase();
  
  // Recherche dans les champs principaux
  if (project.title?.toLowerCase().includes(searchTerm)) return true;
  if (project.acronym?.toLowerCase().includes(searchTerm)) return true;
  if (project.type?.toLowerCase().includes(searchTerm)) return true;
  if (project.description?.toLowerCase().includes(searchTerm)) return true;
  
  // Recherche dans l'UUID
  if (project.uuid?.toLowerCase().includes(searchTerm)) return true;
  
  return false;
};

// Filtrage et tri avec types stricts
const filteredProjects: ComputedRef<Project[]> = computed(() => {
  let result: Project[] = [...props.projects];

  // Filtrage par types sélectionnés
  if (selectedTypes.value.length > 0) {
    result = result.filter((project: Project): boolean => 
      selectedTypes.value.includes(project.type || '')
    );
  }

  // Filtrage par recherche
  if (searchQuery.value) {
    result = result.filter((project: Project): boolean => 
      searchInProject(project, searchQuery.value)
    );
  }

  // Tri
  result.sort((a: Project, b: Project): number => {
    let comparison: number = 0;
    
    switch (sortBy.value) {
      case 'name': {
        const aName = a.acronym || a.title || '';
        const bName = b.acronym || b.title || '';
        comparison = aName.localeCompare(bName);
        break;
      }
      case 'date':
        comparison = new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        break;
      case 'memos': {
        // Note: Le type Project n'a pas de propriété memos pour l'instant
        // TODO: Utiliser une relation Project-Memos si disponible
        comparison = 0;
        break;
      }
    }
    
    return sortDirection.value === 'asc' ? comparison : -comparison;
  });

  return result;
});

// Pagination avec types stricts
const totalPages: ComputedRef<number> = computed(() => 
  Math.ceil(filteredProjects.value.length / props.itemsPerPage)
);

const paginatedProjects: ComputedRef<Project[]> = computed(() => {
  const start: number = (currentPage.value - 1) * props.itemsPerPage;
  const end: number = start + props.itemsPerPage;
  return filteredProjects.value.slice(start, end);
});

// Watchers avec types stricts
watch([searchQuery, selectedTypes, sortBy], (): void => {
  currentPage.value = 1;
});
</script>

<style scoped>
.anamnesis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.anamnesis-table {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.view-transition {
  transition: opacity 0.2s ease-in-out;
}
</style> 